import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { AuthorizationService } from '../services/authorization.service';
import { UserService } from '../../../services/user.service';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { LookupService } from '../../../services/lookup.service';
import { LookupValueItem } from '../../../interfaces/lookup.interface';
import { ProviderService } from '../../../services/provider.service';
import { ButtonComponent } from '@ppl/ui';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { ClaimsService } from '../../../services/claims.service';
import { CategorySpendRow, ConsumerAuthList, SpendingPlanFilters } from '../models/authorization.model';
import { SpendingPlanDetailedSummaryComponent } from './spending-plan-detailed-summary/spending-plan-detailed-summary.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { DialogConfig, DialogService } from '../../../shared/services/dialog.service';
import { SpendingPlanOverviewComponent } from './spending-plan-overview/spending-plan-overview.component';
import { SpendingDashboardHelpComponent } from './spending-dashboard-help/spending-dashboard-help.component';

@Component({
  selector: 'ppl-spending-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,
    ButtonComponent, NzOptionComponent, NzFormControlComponent, NzFormModule,
    NzSelectComponent, SpendingPlanDetailedSummaryComponent,
    NzModalModule, NzDatePickerModule, SpendingPlanOverviewComponent, SpendingDashboardHelpComponent],
  templateUrl: './spending-plan.component.html',
  styleUrl: './spending-plan.component.css'
})
export class SpendingPlanComponent implements OnInit {

  @Input() selectedParticipantId: number = 0;
  @Input() authorizations: ConsumerAuthList[] = [];
  @Input() spentCurrentMonth: number = 0;

  @Output() filtersChange = new EventEmitter<SpendingPlanFilters>();
  @Output() hasAllocationChange = new EventEmitter<boolean>();

  @ViewChild('spendingPlanDetailSummary') spendingPlanDetailSummary!: TemplateRef<any>;
  @ViewChild('spendingDashboardHelp') spendingDashboardHelp!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);
  private lookupService = inject(LookupService);
  private dateFormatter = inject(DateTimeFormatterService);
  private destroyRef = inject(DestroyRef);

  categoryBreakdown: CategorySpendRow[] = [];
  totalBudget: any;
  chartSummary: any = {};
  spendingCategory: any;
  programName: string = '';
  spendingPlan: string | null = null;
  providerId: number | null = null;
  serviceCodeLkp: string | null = null;
  serviceGroupLkp: string | null = null;
  fromDate: Date | null = null;
  toDate: Date | null = null;
  serviceGroups: LookupValueItem[] = [];
  serviceCodes: any[] = [];
  serviceCodesList: any[] = [];
  filterForm!: FormGroup;
  providers: any[] = [];
  noAuthAllocation = false;
  dialogId: string = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthorizationService,
    private userService: UserService,
    private providerService: ProviderService,
    private claimsService: ClaimsService
  ) {}

  ngOnInit(): void {
    this.filterForm = this.fb.group({
      spendingPlan: [null],
      providerId: [null],
      serviceGroupLkp: [null],
      serviceCodeLkp: [null],
      fromDate: [new Date(), Validators.required],
      toDate: [new Date(), [Validators.required, this.toDateNotBeforeFrom]]
    });
    this.fromDate = this.filterForm.get('fromDate')?.value;
    this.toDate = this.filterForm.get('toDate')?.value;

    // The range check lives on To Date, so re-run it whenever From Date moves.
    this.filterForm.get('fromDate')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.filterForm.get('toDate')?.updateValueAndValidity({ emitEvent: false }));

    const program = this.userService.getSelectedProgram();
    this.programName = program?.program_code ?? '';
    this.serviceGroups = this.lookupService.getLookupValue('Service_Group');

    this.filterForm.get('serviceGroupLkp')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.filterServiceCodes());

    this.getServiceData();
    this.getProviders();
    this.loadCategoryChartData();
    this.publishFilters();
  }

  /*
   * Everything scoped by these filters - the monthly breakdown and its table -
   * refreshes from here. Published straight from the user's action so a slow or
   * failed budget request can never leave the lower half showing another slice.
   */
  private publishFilters() {
    this.filtersChange.emit(this.currentFilters());
  }

  private currentFilters(): SpendingPlanFilters {
    return {
      clientAuthNumber: this.spendingPlan ?? null,
      providerId: this.providerId ?? null,
      code: this.serviceCodeLkp ?? null,
      servicegroupLkp: this.serviceGroupLkp ?? null,
      fromDate: this.fromDate ? this.dateFormatter.formatToString(this.fromDate) : null,
      toDate: this.toDate ? this.dateFormatter.formatToString(this.toDate) : null
    };
  }

  /*
   * The applied (not just selected) filters, shown as "Showing results for" so the
   * figures below can always be traced to their criteria. Names are looked up from the
   * dropdown lists, falling back to the raw value until those lists load.
   */
  get appliedFilterChips(): { label: string; value: string }[] {
    const chips: { label: string; value: string }[] = [];
    if (this.spendingPlan) {
      chips.push({ label: 'Spending Plan', value: this.spendingPlan });
    }
    if (this.providerId != null) {
      const provider = this.providers.find(p => p.providerId === this.providerId);
      chips.push({
        label: 'Provider',
        value: provider ? `${provider.providerPplId} - ${provider.providerFullName}` : String(this.providerId)
      });
    }
    if (this.serviceGroupLkp) {
      chips.push({ label: 'Category', value: this.serviceGroupLkp });
    }
    if (this.serviceCodeLkp) {
      const serviceCode = this.serviceCodes.find(sc => sc.code === this.serviceCodeLkp);
      chips.push({
        label: 'Service Code',
        value: serviceCode?.description ? `${this.serviceCodeLkp} - ${serviceCode.description}` : this.serviceCodeLkp
      });
    }
    return chips;
  }

  /** Keeps the range ordered: From can't pass To, and To can't precede From. */
  disableFromDate = (current: Date): boolean => {
    const toDate: Date | null = this.filterForm?.get('toDate')?.value;
    return !!toDate && this.dateOnly(current) > this.dateOnly(toDate);
  };

  disableToDate = (current: Date): boolean => {
    const fromDate: Date | null = this.filterForm?.get('fromDate')?.value;
    return !!fromDate && this.dateOnly(current) < this.dateOnly(fromDate);
  };

  /*
   * The pickers already grey out out-of-order dates, but a typed date skips that,
   * so the form checks the order as well.
   */
  private toDateNotBeforeFrom = (control: AbstractControl): ValidationErrors | null => {
    const fromDate: Date | null = control.parent?.get('fromDate')?.value;
    const toDate: Date | null = control.value;
    return fromDate && toDate && this.dateOnly(toDate) < this.dateOnly(fromDate)
      ? { beforeFromDate: true }
      : null;
  };

  private dateOnly(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }

  getProviders() {
    this.providerService
      .getProviderList(this.programName, this.selectedParticipantId)
      .subscribe((data: any) => {
        this.providers = data.responsedata;
      });
  }

  getServiceData() {
    this.claimsService.getServiceData(this.programName).subscribe((data: any) => {
      this.serviceCodes = data?.responsedata?.serviceCodes ?? [];
      this.filterServiceCodes();
    })
  }

  /*
   * Narrows the Service Code options to the selected Category. Both the dropdown and
   * the service codes carry the group's name (e.g. "Living Arrangement"). A service code
   * that no longer fits the category is cleared.
   */
  private filterServiceCodes() {
    const groupName = this.filterForm.get('serviceGroupLkp')?.value;
    this.serviceCodesList = groupName
      ? this.serviceCodes.filter(sc => sc.servicegroupLkp === groupName)
      : this.serviceCodes;

    const serviceCodeCtrl = this.filterForm.get('serviceCodeLkp');
    if (serviceCodeCtrl?.value && !this.serviceCodesList.some(sc => sc.code === serviceCodeCtrl.value)) {
      serviceCodeCtrl.setValue(null);
    }
  }

  loadCategoryChartData() {
    const request = {
      participantId: this.selectedParticipantId,
      ...this.currentFilters()
    };

    this.authService.getCategoryChartData(this.programName, request).subscribe((data: any) => {
      this.spendingCategory = data.responsedata;

      this.categoryBreakdown = this.spendingCategory?.length ? this.buildCategoryBreakdown() : [];
      this.setTotalBudget();
    });
  }

  /*
   * Headline totals are summed from the filtered budget lines. spending-budget-total
   * ignores the filters, so it would disagree with the category and monthly figures.
   */
  private setTotalBudget() {
    const lines: any[] = this.spendingCategory ?? [];
    this.totalBudget = {
      total: lines.reduce((sum, line) => sum + (line.totalAuth ?? 0), 0),
      spent: lines.reduce((sum, line) => sum + (line.spent ?? 0), 0),
      remaining: lines.reduce((sum, line) => sum + (line.remaining ?? 0), 0)
    };
    this.noAuthAllocation = !this.totalBudget.total;
    this.hasAllocationChange.emit(!this.noAuthAllocation);
  }

  onFilterApply() {
    if (this.filterForm.invalid) {
      // Surface the messages on fields the user hasn't touched yet.
      ['fromDate', 'toDate'].forEach(name => {
        const control = this.filterForm.get(name);
        control?.markAsDirty();
        control?.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    this.spendingPlan = this.filterForm.get('spendingPlan')?.value;
    this.providerId = this.filterForm.get('providerId')?.value;
    this.serviceCodeLkp = this.filterForm.get('serviceCodeLkp')?.value;
    this.serviceGroupLkp = this.filterForm.get('serviceGroupLkp')?.value;
    this.fromDate = this.filterForm.get('fromDate')?.value;
    this.toDate = this.filterForm.get('toDate')?.value;
    this.loadCategoryChartData();
    this.publishFilters();
  }

  onResetFilters() {
    this.filterForm.reset({ fromDate: new Date(), toDate: new Date() });
    this.spendingPlan = null;
    this.providerId = null;
    this.serviceCodeLkp = null;
    this.serviceGroupLkp = null;
    this.fromDate = this.filterForm.get('fromDate')?.value;
    this.toDate = this.filterForm.get('toDate')?.value;
    this.loadCategoryChartData();
    this.publishFilters();
  }

  private buildCategoryBreakdown(): CategorySpendRow[] {
    const categoryMap: Map<string, { spent: number, remaining: number, budgeted: number }> = new Map();

    // Aggregate by category
    this.spendingCategory.forEach((category: any) => {
      let categoryName = category.category
      const serviceGroup = this.serviceGroups.filter(sg => sg.lkpValueCode === category.category);
      if (serviceGroup && serviceGroup.length) {
        categoryName = serviceGroup[0].lkpValueName;
      }

      const current = categoryMap.get(categoryName);
      if (!current) {
        categoryMap.set(categoryName, {
          spent: category.spent ?? 0,
          remaining: category.remaining ?? 0,
          budgeted: category.totalAuth ?? 0
        });
      } else {
        current.spent += category.spent ?? 0;
        current.remaining += category.remaining ?? 0;
        current.budgeted += category.totalAuth ?? 0;
      }

      category.categoryName = categoryName;
    });

    return Array.from(categoryMap.entries())
      .map(([name, totals]) => ({
        name,
        budgeted: totals.budgeted,
        spent: totals.spent,
        remaining: totals.remaining,
        percentUsed: totals.budgeted ? (totals.spent / totals.budgeted) * 100 : null
      }))
      .sort((a, b) => b.budgeted - a.budgeted);
  }

  onAllDetailsSelect() {
    const selectedCategory = this.serviceGroupLkp ? this.serviceGroups.find(sg => sg.lkpValueName === this.serviceGroupLkp)
      : null;
    const availableCategories = this.serviceGroupLkp ? [selectedCategory] : this.serviceGroups;

    this.setChartSummary(availableCategories);
    this.openDetailSummaryModal('My Spending Plan Details');
  }

  onCategorySelect(row: CategorySpendRow) {
    const selectedCategoryCode = this.serviceGroups.find(sg => sg.lkpValueName === row.name);
    this.setChartSummary([selectedCategoryCode]);
    this.openDetailSummaryModal('Allocation Details for Category: ' + row.name);
  }

  openDetailSummaryModal(title: string) {
    const dialogConfig: DialogConfig = {
      title: title,
      okText: 'Close',
      size: 'large-scroll',
      position: 'top',
      showClose: true,
      showButtons: false,
    }

    this.dialogId = this.dialogSvc.openDialog(
      this.spendingPlanDetailSummary, 
      dialogConfig,
      () => this.handleCancelModal(),
      () => this.handleCancelModal()
    );
  }

  openHelpModal() {
    this.dialogId = this.dialogSvc.openDialog(
      this.spendingDashboardHelp,
      {
        title: 'How to Use Your Spending Dashboard',
        size: 'large-scroll',
        position: 'top',
        showClose: true,
        showButtons: false,
      },
      () => this.handleCancelModal(),
      () => this.handleCancelModal()
    );
  }

  /*
   * consumer_auth_spend may carry the category as the lookup code or as its name
   * (e.g. "Living Arrangement"), so a line belongs to a group if either matches.
   */
  private isInServiceGroup(line: any, group: any): boolean {
    return !!group && (line.category === group.lkpValueCode || line.category === group.lkpValueName);
  }

  setChartSummary(categories: any[]) {
    categories = categories.filter(Boolean);
    const lines: any[] = this.spendingCategory ?? [];
    const selectedData = lines.filter((sc: any) => categories.some((c: any) => this.isInServiceGroup(sc, c)));

    this.chartSummary.totalBudgeted = selectedData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.totalAuth ?? 0), 0);
    this.chartSummary.totalSpent = selectedData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.spent ?? 0), 0);
    this.chartSummary.totalRemaining = selectedData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.remaining ?? 0), 0);
    this.chartSummary.categoryGroupsSummary = [];

    categories.forEach((category: any) => {
      const categoryGroupSummary: any = {};
      const categoryData = selectedData.filter((sd: any) => this.isInServiceGroup(sd, category));
      categoryGroupSummary.category = category?.lkpValueName;
      categoryGroupSummary.totalBudgeted = categoryData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.totalAuth ?? 0), 0);
      categoryGroupSummary.totalSpent = categoryData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.spent ?? 0), 0);
      categoryGroupSummary.totalRemaining = categoryData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.remaining ?? 0), 0);
      categoryGroupSummary.totalItems = categoryData.length;
      categoryGroupSummary.servicesSummary = [];
      const availableServiceodes = [...new Set(categoryData.map((cd: any) => cd.code))];
      availableServiceodes.forEach((scode: any) => {
        const serviceData = categoryData.filter((cd: any) => cd.code === scode);
        const serviceSummary: any = {};
        serviceSummary.serviceCode = scode;
        serviceSummary.data = serviceData;
        serviceSummary.totalBudgeted = serviceData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.totalAuth ?? 0), 0);
        serviceSummary.totalSpent = serviceData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.spent ?? 0), 0);
        serviceSummary.totalRemaining = serviceData.reduce((totalAuth: number, sd: any) => totalAuth + (sd.remaining ?? 0), 0);
        serviceSummary.totalItems = serviceData.length;
        categoryGroupSummary.servicesSummary.push(serviceSummary);
      });
      this.chartSummary.categoryGroupsSummary.push(categoryGroupSummary);
    });
  }

  handleCancelModal() {
    this.dialogSvc.closeDialog(this.dialogId);
    this.dialogId = '';
  }
}
